import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { clearCartAsync, updateCartItemAsync, removeFromCartAsync, updateQuantity, removeFromCart } from '../features/cart/cartSlice';
import { createOrder } from '../features/order/orderSlice';
import { fetchPaymentMethods } from '../features/paymentMethods/paymentMethodsSlice';
import Header from './Header';
import { API_URL, BASE_URL, BROKEN_IMAGE_PLACEHOLDER, getImageUrl, normalizeList } from '../config';
import './CheckoutPage.css';

const getAuthHeader = () => {
  const token = localStorage.getItem('jwtToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const CheckoutPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cartItems = useSelector((state) => state.cart.items);
  const orderStatus = useSelector((state) => state.order.status);
  const orderError = useSelector((state) => state.order.error);
  const paymentMethods = useSelector((state) => state.paymentMethods.items);
  const paymentMethodsStatus = useSelector((state) => state.paymentMethods.status);
  const paymentMethodsError = useSelector((state) => state.paymentMethods.error);

  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [pointsBalance, setPointsBalance] = useState(0);
  const [pointsPerRupee, setPointsPerRupee] = useState(10);
  const [pointsToRedeem, setPointsToRedeem] = useState(0);
  const [benefits, setBenefits] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [selectedVoucherCode, setSelectedVoucherCode] = useState('');
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const userRole = useSelector((state) => state.auth.userRole);

  const isCodMethod = (method) => {
    const name = String(method?.methodName || method?.MethodName || '').toLowerCase();
    if (!name || name.includes('qr')) return false;
    return name.includes('cod') || name.includes('cash on delivery');
  };
  const visiblePaymentMethods = normalizeList(paymentMethods).filter(isCodMethod);

  useEffect(() => {
    if (paymentMethodsStatus === 'idle') {
      dispatch(fetchPaymentMethods());
    }
  }, [paymentMethodsStatus, dispatch]);

  useEffect(() => {
    const methods = visiblePaymentMethods;
    const selectedIsVisible = methods.some((m) => m.paymentMethodId === selectedPaymentMethodId);
    if (methods.length > 0 && (selectedPaymentMethodId === null || !selectedIsVisible)) {
      setSelectedPaymentMethodId(methods[0].paymentMethodId);
    }
  }, [paymentMethods, selectedPaymentMethodId]);

  useEffect(() => {
    setPaymentConfirmed(false);
  }, [selectedPaymentMethodId]);

  useEffect(() => {
    const fetchPointsData = async () => {
      if (userRole === 'Admin' || userRole === 'Seller') return;
      try {
        const [configRes, balanceRes, benefitsRes, vouchersRes] = await Promise.all([
          axios.get(`${API_URL}/Points/redemption-config`),
          axios.get(`${API_URL}/Points/my-balance`, { headers: getAuthHeader() }),
          axios.get(`${API_URL}/Points/my-benefits`, { headers: getAuthHeader() }),
          axios.get(`${API_URL}/Points/my-vouchers`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
        ]);
        setPointsPerRupee(configRes.data?.pointsPerRupee ?? 10);
        setPointsBalance(balanceRes.data?.currentBalance ?? 0);
        setBenefits(normalizeList(benefitsRes.data?.benefits));
        setVouchers(Array.isArray(vouchersRes.data) ? vouchersRes.data : vouchersRes.data?.$values ?? []);
      } catch {
        // Ignore - user may not have points
      }
    };
    fetchPointsData();
  }, [userRole]);

  const totalAmount = cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  const deliveryCharges = cartItems.reduce((sum, item) => sum + (Number(item.deliveryCharge) || 0), 0);
  const benefitsList = Array.isArray(benefits) ? benefits : [];
  const freeShippingAllowance = benefitsList.reduce((sum, b) => {
    if (b.benefitType === 'FreeShipping') return sum + (Number(b.benefitValue) || 0);
    return sum;
  }, 0);
  const benefitDiscountEst = benefitsList.reduce((sum, b) => {
    if (b.benefitType === 'ExtraDiscountPercent') return sum + totalAmount * (b.benefitValue / 100);
    if (b.benefitType === 'FixedDiscount') return sum + Math.min(b.benefitValue, totalAmount - sum);
    return sum;
  }, 0);
  const amountAfterBenefits = Math.max(0, totalAmount - benefitDiscountEst);
  const maxPointsDiscount = amountAfterBenefits;
  const maxPointsToRedeem = Math.min(pointsBalance, Math.floor(maxPointsDiscount * pointsPerRupee));
  const pointsDiscount = Math.min((pointsToRedeem || 0) / pointsPerRupee, amountAfterBenefits);
  const amountAfterPoints = Math.max(0, amountAfterBenefits - pointsDiscount);
  const selectedVoucher = vouchers.find(v => v.code === selectedVoucherCode);
  const voucherDiscount = selectedVoucher
    ? Math.min(Number(selectedVoucher.discountAmount) || 0, amountAfterPoints)
    : 0;
  const payableDelivery = Math.max(0, deliveryCharges - freeShippingAllowance);
  const finalTotal = Math.max(0, amountAfterPoints - voucherDiscount + payableDelivery);
  const totalMRP = cartItems.reduce((total, item) => {
    const mrp = item.originalPrice != null && Number(item.originalPrice) > Number(item.price) ? Number(item.originalPrice) : item.price;
    return total + mrp * item.quantity;
  }, 0);
  const totalDiscount = totalMRP - totalAmount;
  const totalItems = cartItems.reduce((total, item) => total + item.quantity, 0);
  const selectedPaymentMethod = visiblePaymentMethods
    .find((m) => m.paymentMethodId === selectedPaymentMethodId);
  const paymentMethodName = (selectedPaymentMethod?.methodName || '').toLowerCase();
  const isCodPayment = paymentMethodName.includes('cod') || paymentMethodName.includes('cash on delivery');
  const needsPaymentFirst = Boolean(selectedPaymentMethodId) && !isCodPayment;

  // Calculate expected delivery date (7 days from now)
  const getExpectedDelivery = () => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date.toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Calculate discount percentage (database-driven from originalPrice)
  const getDiscountPercent = (price, originalPrice) => {
    const mrp = originalPrice != null && Number(originalPrice) > Number(price) ? Number(originalPrice) : price;
    return Math.round(((mrp - price) / mrp) * 100);
  };

  // Handle quantity change
  const handleQuantityChange = (item, newQuantity) => {
    if (newQuantity < 1) return;
    const maxStock = Number(item.stock);
    if (Number.isFinite(maxStock) && newQuantity > maxStock) {
      const variantLabel = [item.colorName, item.sizeName].filter(Boolean).join(' ');
      window.alert(variantLabel ? `Only ${maxStock} in stock for ${variantLabel}.` : `Only ${maxStock} in stock.`);
      return;
    }
    const token = localStorage.getItem('jwtToken');
    if (token) {
      dispatch(updateCartItemAsync({ productId: item.productId, quantity: newQuantity, variantId: item.variantId ?? null }));
    } else {
      dispatch(updateQuantity({ productId: item.productId, quantity: newQuantity, variantId: item.variantId ?? null }));
    }
  };

  // Handle remove item
  const handleRemoveItem = (item) => {
    const token = localStorage.getItem('jwtToken');
    if (token) {
      dispatch(removeFromCartAsync({ productId: item.productId, variantId: item.variantId ?? null }));
    } else {
      dispatch(removeFromCart({ productId: item.productId, variantId: item.variantId ?? null }));
    }
  };

  // Handle save for later (for now just remove from cart)
  const handleSaveForLater = (item) => {
    alert(`"${item.productName}" saved for later!`);
    handleRemoveItem(item);
  };

  const handlePlaceOrder = async () => {
    if (cartItems.length === 0) {
      alert('Your cart is empty. Please add items before checking out.');
      return;
    }

    if (selectedPaymentMethodId === null) {
      alert('Please select a payment method.');
      return;
    }

    if (needsPaymentFirst && !paymentConfirmed) {
      alert('Please complete payment and confirm it before placing the order.');
      return;
    }

    const orderItems = cartItems.map(item => ({
      productId: item.productId,
      quantity: item.quantity,
      productSellerId: item.productSellerId || undefined,
      variantId: item.variantId || undefined
    }));

    try {
      const resultAction = await dispatch(createOrder({
        items: orderItems,
        paymentMethodId: selectedPaymentMethodId,
        pointsToRedeem: pointsToRedeem || 0,
        voucherCode: selectedVoucherCode || null
      }));

      if (createOrder.fulfilled.match(resultAction)) {
        alert('Order placed successfully!');
        dispatch(clearCartAsync());
        navigate('/');
      } else {
        alert(`Failed to place order: ${orderError || 'Unknown error'}`);
      }
    } catch (error) {
      alert(`An error occurred while placing the order: ${error.message}`);
    }
  };

  let paymentMethodsContent;
  if (paymentMethodsStatus === 'loading') {
    paymentMethodsContent = <div>Loading payment methods...</div>;
  } else if (paymentMethodsStatus === 'succeeded') {
    const methods = visiblePaymentMethods;
    paymentMethodsContent = (
      <div className="payment-method-section">
        {methods.map((method) => (
          <label key={method.paymentMethodId ?? method.PaymentMethodId}>
            <input
              type="radio"
              value={method.paymentMethodId ?? method.PaymentMethodId}
              checked={selectedPaymentMethodId === (method.paymentMethodId ?? method.PaymentMethodId)}
              onChange={() => setSelectedPaymentMethodId(method.paymentMethodId ?? method.PaymentMethodId)}
            />
            {method.methodName || method.MethodName}
          </label>
        ))}
        {methods.length === 0 && (
          <p className="payment-cod-note">Cash on Delivery is currently unavailable. Please try again later.</p>
        )}
        {needsPaymentFirst && (
          <label className="payment-confirm-label">
            <input
              type="checkbox"
              checked={paymentConfirmed}
              onChange={(e) => setPaymentConfirmed(e.target.checked)}
            />
            I have completed the payment of ₹{finalTotal.toFixed(0)}
          </label>
        )}
        {isCodPayment && (
          <p className="payment-cod-note">Cash on Delivery: you can place the order now and pay when it arrives.</p>
        )}
        <button
          className="place-order-button"
          onClick={handlePlaceOrder}
          disabled={
            cartItems.length === 0
            || orderStatus === 'loading'
            || selectedPaymentMethodId === null
            || (needsPaymentFirst && !paymentConfirmed)
          }
        >
          {orderStatus === 'loading'
            ? 'Placing Order...'
            : isCodPayment
              ? 'PLACE ORDER'
              : 'PAY & PLACE ORDER'}
        </button>
      </div>
    );
  } else if (paymentMethodsStatus === 'failed') {
    paymentMethodsContent = <div className="error-message">Error loading payment methods: {paymentMethodsError ?? orderError ?? 'Unknown error'}</div>;
  }

  return (
    <div className="checkout-wrapper">
      <Header />
      <div className="checkout-page-container">
        <div className="checkout-main">
          {/* Left Section - Order Items */}
          <div className="checkout-left">
            <div className="checkout-section">
              <div className="section-header">
                <h2>Order Summary</h2>
                <span className="item-count">{totalItems} {totalItems === 1 ? 'Item' : 'Items'}</span>
              </div>

              {cartItems.length === 0 ? (
                <div className="empty-cart-message">
                  <p>Your cart is empty.</p>
                  <button onClick={() => navigate('/')} className="continue-shopping-btn">
                    Continue Shopping
                  </button>
                </div>
              ) : (
                <div className="checkout-items-list">
                  {cartItems.map((item) => {
                    const mrp = item.originalPrice != null && Number(item.originalPrice) > Number(item.price) ? Number(item.originalPrice) : item.price;
                    const discountPercent = getDiscountPercent(item.price, item.originalPrice);
                    const showDiscount = mrp > item.price && discountPercent > 0;
                    
                    return (
                      <div key={`${item.productId}-${item.variantId ?? 'none'}`} className="checkout-item-card">
                        <div className="item-image-container">
                          <img 
                            src={getImageUrl(item.imageUrl)} 
                            alt={item.productName}
                            onError={(e) => { e.target.src = BROKEN_IMAGE_PLACEHOLDER; }}
                          />
                        </div>
                        <div className="item-details">
                          <h3 className="item-name">{item.productName}</h3>
                          {(item.sizeName || item.colorName) && (
                            <p className="item-variant">
                              {[item.sizeName && `Size: ${item.sizeName}`, item.colorName && `Color: ${item.colorName}`].filter(Boolean).join(' · ')}
                            </p>
                          )}
                          
                          <div className="item-seller">
                            Seller: {item.sellerName || 'Yelooo Store'}
                            {item.sellerCode ? ` (${item.sellerCode})` : ''}
                            <span className="assured-badge">
                              <svg viewBox="0 0 24 24" fill="#2874f0" width="14" height="14">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                              </svg>
                              Assured
                            </span>
                          </div>

                          <div className="item-pricing">
                            {showDiscount && <span className="item-mrp">₹{mrp.toFixed(0)}</span>}
                            <span className="item-price">₹{item.price?.toFixed(0)}</span>
                            {showDiscount && <span className="item-discount">{discountPercent}% Off</span>}
                          </div>

                          <div className="item-actions">
                            <div className="quantity-selector">
                              <button 
                                className="qty-btn delete-btn"
                                onClick={() => handleRemoveItem(item)}
                                title="Remove from cart"
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                  <polyline points="3 6 5 6 21 6"></polyline>
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                </svg>
                              </button>
                              <span className="qty-value">{item.quantity}</span>
                              <button 
                                className="qty-btn add-btn"
                                onClick={() => handleQuantityChange(item, item.quantity + 1)}
                                title="Add more"
                                disabled={Number.isFinite(Number(item.stock)) && item.quantity >= Number(item.stock)}
                              >
                                +
                              </button>
                            </div>
                            <button className="action-btn save-btn" onClick={() => handleSaveForLater(item)}>
                              Save for later
                            </button>
                            <button className="action-btn remove-btn" onClick={() => handleRemoveItem(item)}>
                              Remove
                            </button>
                          </div>
                        </div>
                        <div className="item-delivery">
                          <span className="delivery-label">Delivery by</span>
                          <span className="delivery-date">{getExpectedDelivery()}</span>
                          {Number(item.deliveryCharge) > 0 ? (
                            <span className="item-delivery-fee">₹{Number(item.deliveryCharge).toFixed(0)} delivery</span>
                          ) : (
                            <span className="item-delivery-fee free">FREE delivery</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Payment Method Section */}
            {cartItems.length > 0 && (
              <div className="checkout-section payment-section">
                <div className="section-header">
                  <h2>Payment Method</h2>
                </div>
                {paymentMethodsContent}
              </div>
            )}
          </div>

          {/* Right Section - Price Details */}
          {cartItems.length > 0 && (
            <div className="checkout-right">
              <div className="price-details-card">
                <h3>Price Details</h3>
                <div className="price-row">
                  <span>Price ({totalItems} {totalItems === 1 ? 'item' : 'items'})</span>
                  <span>₹{totalMRP.toFixed(0)}</span>
                </div>
                {totalDiscount > 0 && (
                  <div className="price-row discount-row">
                    <span>Discount</span>
                    <span className="discount-amount">− ₹{totalDiscount.toFixed(0)}</span>
                  </div>
                )}
                {benefitDiscountEst > 0 && (
                  <div className="price-row discount-row">
                    <span>Points benefit</span>
                    <span className="discount-amount">− ₹{benefitDiscountEst.toFixed(0)}</span>
                  </div>
                )}
                {userRole !== 'Admin' && userRole !== 'Seller' && pointsBalance > 0 && (
                  <div className="price-row points-redeem-row">
                    <div>
                      <span>Use points</span>
                      <span className="points-available">(Available: {pointsBalance.toFixed(0)})</span>
                    </div>
                    <div className="points-input-wrap">
                      <input
                        type="number"
                        min="0"
                        max={maxPointsToRedeem}
                        value={pointsToRedeem || ''}
                        onChange={(e) => setPointsToRedeem(Math.min(parseFloat(e.target.value) || 0, maxPointsToRedeem))}
                        placeholder="0"
                      />
                      <span className="points-hint">= ₹{(pointsToRedeem / pointsPerRupee || 0).toFixed(0)} off</span>
                    </div>
                  </div>
                )}
                {pointsDiscount > 0 && (
                  <div className="price-row discount-row">
                    <span>Points redeemed</span>
                    <span className="discount-amount">− ₹{pointsDiscount.toFixed(0)}</span>
                  </div>
                )}
                {userRole !== 'Admin' && userRole !== 'Seller' && vouchers.length > 0 && (
                  <div className="price-row voucher-row">
                    <div>
                      <span>Wallet voucher</span>
                      <select
                        value={selectedVoucherCode}
                        onChange={(e) => setSelectedVoucherCode(e.target.value)}
                        className="voucher-select"
                      >
                        <option value="">None</option>
                        {vouchers.map((v) => (
                          <option key={v.id} value={v.code}>
                            {v.code} (−₹{Number(v.discountAmount).toFixed(0)})
                          </option>
                        ))}
                      </select>
                    </div>
                    {voucherDiscount > 0 && (
                      <span className="discount-amount">− ₹{voucherDiscount.toFixed(0)}</span>
                    )}
                  </div>
                )}
                {voucherDiscount > 0 && (
                  <div className="price-row discount-row">
                    <span>Voucher discount</span>
                    <span className="discount-amount">− ₹{voucherDiscount.toFixed(0)}</span>
                  </div>
                )}
                <div className="price-row">
                  <span>Delivery Charges</span>
                  {payableDelivery > 0 ? (
                    <span>₹{payableDelivery.toFixed(0)}</span>
                  ) : (
                    <span className="free-delivery">FREE</span>
                  )}
                </div>
                <div className="price-row total-row">
                  <span>Total Amount</span>
                  <span>₹{finalTotal.toFixed(0)}</span>
                </div>
                {(totalDiscount > 0 || benefitDiscountEst > 0 || pointsDiscount > 0 || voucherDiscount > 0) && (
                  <div className="savings-banner">
                    You will save ₹{(totalDiscount + benefitDiscountEst + pointsDiscount + voucherDiscount).toFixed(0)} on this order
                  </div>
                )}

                <p className="checkout-pay-first-hint">
                  {needsPaymentFirst
                    ? 'Complete payment on the left, then confirm it to place your order.'
                    : 'Select a payment method, then place your order.'}
                </p>

                {orderStatus === 'failed' && <p className="error-message">Error: {orderError}</p>}

                <div className="secure-badge">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                  </svg>
                  <span>Safe and Secure Payments. Easy returns. 100% Authentic products.</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
